import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, LineChart } from "lucide-react";
import { crmApi } from "@/lib/ownerClient";

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value ?? "—"}</div>
      {hint && <div className="mt-1 text-xs text-zinc-400">{hint}</div>}
    </div>
  );
}

export default function PipelineView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await crmApi.pipelineHealth();
        setData(res);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="owner-pipeline">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline Health</h1>
          <p className="text-sm text-zinc-400 mt-1">Daily project + maintenance plan KPIs side-by-side.</p>
        </div>
        <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid="pipeline-phase-badge">
          Phase A placeholder
        </Badge>
      </div>

      <Card className="bg-amber-500/5 border-amber-500/30">
        <CardContent className="py-4 text-sm text-amber-200 flex items-start gap-2">
          <LineChart className="h-4 w-4 mt-0.5" />
          <span>
            {data?.message || "Pipeline KPIs populate after Phase B wires in lead intake + appointment booking + inbound-shortfall trigger."}
          </span>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500">Project Pipeline</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="New Leads Today" value={data?.project_pipeline?.new_leads_today ?? 0} />
          <Stat label="Appointments · Next 14d" value={data?.project_pipeline?.appointments_next_14d ?? 0} />
          <Stat label="Avg Speed-to-Lead" value={data?.project_pipeline?.speed_to_lead_avg_sec ? `${data.project_pipeline.speed_to_lead_avg_sec}s` : "—"} hint="target <5min" />
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500">Maintenance Plan Pipeline</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="New Plans (MTD)" value={data?.maintenance_pipeline?.new_plans_this_month ?? 0} />
          <Stat label="MRR (USD)" value={`$${(data?.maintenance_pipeline?.mrr_usd ?? 0).toLocaleString()}`} />
          <Stat label="Active Members" value={data?.maintenance_pipeline?.active_members ?? 0} />
          <Stat label="Attach Rate" value={`${data?.maintenance_pipeline?.attach_rate_pct ?? 0}%`} hint="target 40%" />
        </div>
      </section>
    </div>
  );
}
